import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { saveAttachments, invalidateAttachmentCache } from './useChatAttachments';
import type { UploadedFile } from './useChatAttachments';
import { uniqueChannelName } from '../../lib/realtime';

export interface ChatMessageUser {
  id: string;
  name: string;
  avatar: string | null;
  role: string;
}

export interface ChatMessage {
  id: string;
  channel_id: string;
  user_id: string;
  content: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: Record<string, any> | null;
  edited_at: string | null;
  deleted_at: string | null;
  created_at: string;
  user: ChatMessageUser;
  thread_id: string | null;
  reply_count: number;
  last_reply_at: string | null;
}

const PAGE_SIZE = 50;

// Cache user data to avoid repeated lookups
const userCache = new Map<string, ChatMessageUser>();

async function fetchUserInfo(userId: string): Promise<ChatMessageUser> {
  const cached = userCache.get(userId);
  if (cached) return cached;

  const { data } = await supabase
    .from('users')
    .select('id, name, avatar, role')
    .eq('id', userId)
    .single();

  const userInfo: ChatMessageUser = data
    ? { id: data.id, name: data.name || 'Unknown', avatar: data.avatar || null, role: data.role || '' }
    : { id: userId, name: 'Unknown', avatar: null, role: '' };

  userCache.set(userId, userInfo);
  return userInfo;
}

export function useChatMessages(channelId: string | null) {
  const { user } = useUser();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const lastReadUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update last_read_at for the channel (debounced)
  const updateLastRead = useCallback(
    (chId: string) => {
      if (!user || !chId) return;
      if (lastReadUpdateRef.current) {
        clearTimeout(lastReadUpdateRef.current);
      }
      lastReadUpdateRef.current = setTimeout(async () => {
        try {
          await supabase
            .from('chat_channel_members')
            .update({ last_read_at: new Date().toISOString() })
            .eq('channel_id', chId)
            .eq('user_id', user.id);
        } catch (err) {
          console.error('Error updating last_read_at:', err);
        }
      }, 500);
    },
    [user]
  );

  // Fetch initial messages
  const fetchMessages = useCallback(async () => {
    if (!channelId) {
      setMessages([]);
      return;
    }

    setLoading(true);
    setError(null);
    offsetRef.current = 0;

    try {
      const { data, error: fetchError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('channel_id', channelId)
        .is('deleted_at', null)
        .is('thread_id', null) // Only fetch root messages (not thread replies)
        .order('created_at', { ascending: false })
        .range(0, PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      // Fetch user info for all messages
      const userIds = [...new Set((data || []).map(m => m.user_id))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));

      const enrichedMessages: ChatMessage[] = (data || []).map(msg => ({
        ...msg,
        metadata: msg.metadata as ChatMessage['metadata'],
        user: userCache.get(msg.user_id) || {
          id: msg.user_id,
          name: 'Unknown',
          avatar: null,
          role: '',
        },
      }));

      // Reverse so oldest is first (ascending order for display)
      setMessages(enrichedMessages.reverse());
      setHasMore((data || []).length === PAGE_SIZE);
      offsetRef.current = (data || []).length;

      // Mark as read
      updateLastRead(channelId);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError((err as Error).message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [channelId, updateLastRead]);

  // Load more (older messages)
  const loadMore = useCallback(async () => {
    if (!channelId || !hasMore || loading) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('channel_id', channelId)
        .is('deleted_at', null)
        .is('thread_id', null) // Only fetch root messages (not thread replies)
        .order('created_at', { ascending: false })
        .range(offsetRef.current, offsetRef.current + PAGE_SIZE - 1);

      if (fetchError) throw fetchError;

      const userIds = [...new Set((data || []).map(m => m.user_id))];
      await Promise.all(userIds.map(id => fetchUserInfo(id)));

      const enrichedMessages: ChatMessage[] = (data || []).map(msg => ({
        ...msg,
        metadata: msg.metadata as ChatMessage['metadata'],
        user: userCache.get(msg.user_id) || {
          id: msg.user_id,
          name: 'Unknown',
          avatar: null,
          role: '',
        },
      }));

      // Prepend older messages (they're in desc order, reverse them)
      setMessages(prev => [...enrichedMessages.reverse(), ...prev]);
      setHasMore((data || []).length === PAGE_SIZE);
      offsetRef.current += (data || []).length;
    } catch (err) {
      console.error('Error loading more messages:', err);
    }
  }, [channelId, hasMore, loading]);

  // Send a message (with optional file attachments)
  const sendMessage = useCallback(
    async (content: string, attachments?: UploadedFile[]) => {
      if (!channelId || !user) return;

      const hasText = content.trim().length > 0;
      const hasAttachments = attachments && attachments.length > 0;

      // Need either text or attachments
      if (!hasText && !hasAttachments) return;

      try {
        // Build metadata if we have attachments
        const metadata = hasAttachments
          ? {
              attachments: attachments.map((a) => ({
                file_name: a.fileName,
                file_type: a.fileType,
                file_size: a.fileSize,
                public_url: a.publicUrl,
                width_px: a.widthPx,
                height_px: a.heightPx,
              })),
            }
          : null;

        const { data, error: sendError } = await supabase
          .from('chat_messages')
          .insert({
            channel_id: channelId,
            user_id: user.id,
            content: content.trim(),
            ...(metadata ? { metadata } : {}),
          })
          .select('id')
          .single();

        if (sendError) throw sendError;

        // Insert attachments into chat_attachments table
        if (hasAttachments && data?.id) {
          try {
            await saveAttachments(data.id, attachments);
            invalidateAttachmentCache(data.id);
          } catch (attErr) {
            console.error('Error saving attachments (message was sent):', attErr);
          }
        }

        // The realtime subscription will handle adding the message to state
        // Also update last_read_at
        updateLastRead(channelId);
      } catch (err) {
        console.error('Error sending message:', err);
        throw err;
      }
    },
    [channelId, user, updateLastRead]
  );

  // Fetch on channel change
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Edit a message (optimistic update + persist)
  const editMessage = useCallback(
    async (messageId: string, newContent: string) => {
      if (!user) return;
      const now = new Date().toISOString();

      // Optimistic update
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId ? { ...m, content: newContent, edited_at: now } : m
        )
      );

      try {
        const { error: updateError } = await supabase
          .from('chat_messages')
          .update({ content: newContent, edited_at: now })
          .eq('id', messageId)
          .eq('user_id', user.id);

        if (updateError) throw updateError;
      } catch (err) {
        console.error('Error editing message:', err);
        // Revert optimistic update by refetching
        fetchMessages();
        throw err;
      }
    },
    [user, fetchMessages]
  );

  // Soft-delete a message (optimistic update + persist)
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!user) return;
      const now = new Date().toISOString();

      // Optimistic update
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId ? { ...m, deleted_at: now } : m
        )
      );

      try {
        const { error: deleteError } = await supabase
          .from('chat_messages')
          .update({ deleted_at: now })
          .eq('id', messageId)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;
      } catch (err) {
        console.error('Error deleting message:', err);
        // Revert optimistic update by refetching
        fetchMessages();
        throw err;
      }
    },
    [user, fetchMessages]
  );

  // Realtime subscription for new and updated messages
  useEffect(() => {
    if (!channelId) return;

    const subscription = supabase
      .channel(uniqueChannelName(`chat:${channelId}`))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `channel_id=eq.${channelId}`,
        },
        async (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newMsg = payload.new as any;

          // If this is a thread reply, don't add it to the main feed
          // Instead, the trigger will update the parent message's reply_count and last_reply_at
          if (newMsg.thread_id) {
            return;
          }

          // Fetch user info if not cached
          const userInfo = await fetchUserInfo(newMsg.user_id);

          const enrichedMessage: ChatMessage = {
            ...newMsg,
            user: userInfo,
          };

          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === enrichedMessage.id)) return prev;
            return [...prev, enrichedMessage];
          });

          // Update offset to account for new message
          offsetRef.current += 1;

          // Update last_read_at
          updateLastRead(channelId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `channel_id=eq.${channelId}`,
        },
        async (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const updated = payload.new as any;

          setMessages(prev =>
            prev.map(m =>
              m.id === updated.id
                ? { 
                    ...m, 
                    content: updated.content, 
                    edited_at: updated.edited_at, 
                    deleted_at: updated.deleted_at,
                    reply_count: updated.reply_count,
                    last_reply_at: updated.last_reply_at
                  }
                : m
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [channelId, updateLastRead]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (lastReadUpdateRef.current) {
        clearTimeout(lastReadUpdateRef.current);
      }
    };
  }, []);

  return { messages, loading, error, loadMore, hasMore, sendMessage, editMessage, deleteMessage };
}
